import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, Search, Download, Edit3, Trash2, Star } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, money, fmtDate, statusPill, statusLabel, API } from '../../lib/api';
import PullToRefresh from '../../components/PullToRefresh';
import SwipeRow from '../../components/SwipeRow';

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [q, setQ] = useState('');
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [selected, setSelected] = useState(new Set());
  const navigate = useNavigate();

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status === 'vip') {
      params.vip = true;
    } else if (status) {
      params.status = status;
    }
    adminApi.get('/admin/invoices', { params }).then((r) => setInvoices(r.data));
  };
  useEffect(() => { load(); }, [q, status]);

  const toggleVip = async (inv, e) => {
    e?.stopPropagation();
    // Optimistic update
    setInvoices((prev) => prev.map((i) => (i.invoice_id === inv.invoice_id ? { ...i, is_vip: !i.is_vip } : i)));
    try {
      const r = await adminApi.patch(`/admin/invoices/${inv.invoice_id}/vip`);
      // Sync with server response
      setInvoices((prev) => prev.map((i) => (i.invoice_id === inv.invoice_id ? { ...i, is_vip: r.data.is_vip } : i)));
      toast.success(r.data.is_vip ? `${inv.invoice_number} marked as VIP` : `${inv.invoice_number} removed from VIP`);
    } catch {
      // Revert
      setInvoices((prev) => prev.map((i) => (i.invoice_id === inv.invoice_id ? { ...i, is_vip: inv.is_vip } : i)));
      toast.error('Failed to update VIP');
    }
  };

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === invoices.length) setSelected(new Set());
    else setSelected(new Set(invoices.map((i) => i.invoice_id)));
  };

  const bulkDownload = async () => {
    if (selected.size === 0) return toast.error('Select at least one invoice');
    try {
      const token = localStorage.getItem('adminToken');
      const resp = await fetch(`${API}/admin/invoices/bulk-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ invoice_ids: Array.from(selected) }),
      });
      if (!resp.ok) throw new Error('Failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'ottomed_invoices.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${selected.size} invoices`);
    } catch { toast.error('Download failed'); }
  };

  const downloadOne = async (id, num) => {
    try {
      const token = localStorage.getItem('adminToken');
      const resp = await fetch(`${API}/admin/invoices/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${num}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const deleteInvoice = async (inv) => {
    const hasPayments = (inv.paid_amount || 0) > 0;
    const msg = hasPayments
      ? `Invoice ${inv.invoice_number} has ${money(inv.paid_amount)} in recorded payments.\n\nDelete anyway? This will also remove the payment records.`
      : `Delete invoice ${inv.invoice_number}?\n\nThis cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await adminApi.delete(`/admin/invoices/${inv.invoice_id}${hasPayments ? '?force=true' : ''}`);
      toast.success(`Deleted ${inv.invoice_number}`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const withPayments = invoices.filter((i) => ids.includes(i.invoice_id) && (i.paid_amount || 0) > 0).length;
    const msg = withPayments > 0
      ? `Delete ${ids.length} invoices? ${withPayments} have recorded payments that will also be removed.\n\nThis cannot be undone.`
      : `Delete ${ids.length} invoices? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await adminApi.delete(`/admin/invoices/${id}?force=true`);
        ok++;
      } catch { fail++; }
    }
    toast.success(`Deleted ${ok}${fail ? ` · ${fail} failed` : ''}`);
    setSelected(new Set());
    load();
  };

  return (
    <PullToRefresh onRefresh={load}>
    <div className="fade-in space-y-6" data-testid="invoices-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-3xl font-semibold">Orders</h1>
          <p className="text-sm text-[#6b7280] mt-1">{invoices.length} orders · all invoices live here</p>
        </div>
        <Link to="/admin/orders/new" className="btn-brand flex items-center gap-2" data-testid="new-invoice-btn">
          <Plus size={16} /> New Order
        </Link>
      </div>

      <div className="card p-3 sm:p-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-[#6b7280]" />
          <input
            type="text"
            placeholder="Search invoice # or customer name..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="flex-1 border-none outline-none text-sm bg-transparent"
            data-testid="invoice-search"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border border-[#e7e7e7] rounded-lg px-3 py-2" data-testid="invoice-status-filter">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="unpaid">Unpaid</option>
          <option value="partial">Partial</option>
          <option value="paid">Paid</option>
          <option value="overdue">Overdue</option>
          <option value="vip">⭐ VIP only</option>
        </select>
        {selected.size > 0 && (
          <>
            <button onClick={bulkDownload} className="btn-ghost flex items-center gap-2" data-testid="bulk-download-btn">
              <Download size={14} /> Download {selected.size}
            </button>
            <button
              onClick={bulkDelete}
              className="btn-ghost text-[#d92027] flex items-center gap-2"
              data-testid="bulk-delete-btn"
            >
              <Trash2 size={14} /> Delete {selected.size}
            </button>
          </>
        )}
      </div>

      {/* Desktop / tablet: full table */}
      <div className="card overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-[#fafafa] text-[#6b7280] text-[11px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-3 w-10">
                <input type="checkbox" checked={selected.size === invoices.length && invoices.length > 0} onChange={toggleAll} data-testid="select-all-invoices" />
              </th>
              <th className="px-2 py-3 w-10 text-center" title="VIP">⭐</th>
              <th className="text-left px-4 py-3">Invoice</th>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Due</th>
              <th className="text-right px-4 py-3">Total</th>
              <th className="text-right px-4 py-3">Paid</th>
              <th className="text-right px-4 py-3">Balance</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const balance = (inv.total || 0) - (inv.paid_amount || 0);
              return (
                <tr key={inv.invoice_id} className={`border-t border-[#f3f3f3] hover:bg-[#fafafa] cursor-pointer ${inv.is_vip ? 'bg-amber-50/40' : ''}`} onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(inv.invoice_id)} onChange={() => toggle(inv.invoice_id)} data-testid={`select-${inv.invoice_id}`} />
                  </td>
                  <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={(e) => toggleVip(inv, e)}
                      className={`p-1 rounded transition-colors ${inv.is_vip ? 'text-amber-500 hover:text-amber-600' : 'text-[#d1d5db] hover:text-amber-400'}`}
                      data-testid={`vip-${inv.invoice_id}`}
                      title={inv.is_vip ? 'Remove VIP' : 'Mark as VIP'}
                    >
                      <Star size={16} fill={inv.is_vip ? 'currentColor' : 'none'} />
                    </button>
                  </td>
                  <td className="px-4 py-3 mono font-semibold">{inv.invoice_number}</td>
                  <td className="px-4 py-3">{inv.customer_snapshot?.name || '—'}</td>
                  <td className="px-4 py-3 text-xs text-[#6b7280]">{fmtDate(inv.issue_date)}</td>
                  <td className="px-4 py-3 text-xs text-[#6b7280]">{fmtDate(inv.due_date)}</td>
                  <td className="text-right px-4 py-3 mono">{money(inv.total)}</td>
                  <td className="text-right px-4 py-3 mono text-green-700">{money(inv.paid_amount)}</td>
                  <td className="text-right px-4 py-3 mono text-[#d92027] font-semibold">{money(balance)}</td>
                  <td className="text-center px-4 py-3"><span className={statusPill(inv.status)}>{statusLabel(inv.status)}</span></td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-0.5 justify-end">
                      <button
                        onClick={() => downloadOne(inv.invoice_id, inv.invoice_number)}
                        className="p-1.5 hover:bg-white rounded text-[#6b7280]"
                        title="Download PDF"
                        data-testid={`download-${inv.invoice_id}`}
                      >
                        <Download size={14} />
                      </button>
                      <button
                        onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}
                        className="p-1.5 hover:bg-white rounded text-[#6b7280]"
                        title="View / Edit"
                        data-testid={`edit-${inv.invoice_id}`}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={() => deleteInvoice(inv)}
                        className="p-1.5 hover:bg-white rounded text-[#d92027]"
                        title="Delete"
                        data-testid={`delete-${inv.invoice_id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {invoices.length === 0 && <div className="py-10 text-center text-sm text-[#6b7280]">No invoices found.</div>}
      </div>

      {/* Mobile: card list */}
      <div className="md:hidden space-y-3">
        {invoices.length === 0 ? (
          <div className="card py-10 text-center text-sm text-[#6b7280]">No invoices found.</div>
        ) : (
          invoices.map((inv) => {
            const balance = (inv.total || 0) - (inv.paid_amount || 0);
            return (
              <SwipeRow
                key={inv.invoice_id}
                onDelete={() => deleteInvoice(inv)}
                testid={`swipe-inv-${inv.invoice_id}`}
              >
              <div
                className={`card p-4 ${inv.is_vip ? 'ring-1 ring-amber-300 bg-amber-50/30' : ''}`}
                onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}
                data-testid={`mobile-inv-${inv.invoice_id}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={selected.has(inv.invoice_id)}
                      onChange={() => toggle(inv.invoice_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="mt-1"
                      data-testid={`m-select-${inv.invoice_id}`}
                    />
                    <button
                      onClick={(e) => toggleVip(inv, e)}
                      className={`mt-0.5 p-0.5 ${inv.is_vip ? 'text-amber-500' : 'text-[#d1d5db]'}`}
                      data-testid={`m-vip-${inv.invoice_id}`}
                    >
                      <Star size={18} fill={inv.is_vip ? 'currentColor' : 'none'} />
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="mono font-semibold truncate">{inv.invoice_number}</div>
                      <div className="text-sm truncate">{inv.customer_snapshot?.name || '—'}</div>
                      <div className="text-[11px] text-[#6b7280] mt-0.5">
                        Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}
                      </div>
                    </div>
                  </div>
                  <span className={statusPill(inv.status)}>{statusLabel(inv.status)}</span>
                </div>

                <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-[#f3f3f3]">
                  <div>
                    <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Total</div>
                    <div className="mono font-semibold">{money(inv.total)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Paid</div>
                    <div className="mono font-semibold text-green-700">{money(inv.paid_amount)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#9ca3af] tracking-wide">Balance</div>
                    <div className="mono font-semibold text-[#d92027]">{money(balance)}</div>
                  </div>
                </div>

                <div
                  className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-[#f3f3f3]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => downloadOne(inv.invoice_id, inv.invoice_number)}
                    className="p-2 rounded-lg bg-[#fafafa] text-[#6b7280]"
                    data-testid={`m-download-${inv.invoice_id}`}
                  >
                    <Download size={16} />
                  </button>
                  <button
                    onClick={() => navigate(`/admin/orders/${inv.invoice_id}`)}
                    className="p-2 rounded-lg bg-[#fafafa] text-[#6b7280]"
                    data-testid={`m-edit-${inv.invoice_id}`}
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => deleteInvoice(inv)}
                    className="p-2 rounded-lg bg-[#fef2f2] text-[#d92027]"
                    data-testid={`m-delete-${inv.invoice_id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              </SwipeRow>
            );
          })
        )}
      </div>
    </div>
    </PullToRefresh>
  );
}
